import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSetToast } from '@/hooks'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import {
  getPresignedUploadUrl,
  getPresignedUploadUrls,
  processImage,
  processImageBatch,
} from '@/api/caseImages'
import { uploadToPresignedUrl } from '@messenger/utils'
import {
  pollForCompletion,
  pollBatchForCompletion,
} from '@/api/caseImageJobPolling'
import { ERROR_CODE } from '@messenger/consts'
import { validateCaseImageFile } from '@messenger/utils'

type UseCaseImageUploadOptions = {
  caseId: string
  isAdmin: boolean
  componentName?: string
}

type UseCaseImageUploadReturn = {
  uploadImage: (file: File) => Promise<{ imageUrl: string }>
  uploadMultipleImages: (files: File[]) => Promise<string[]>
}

export const useCaseImageUpload = (
  options: UseCaseImageUploadOptions,
): UseCaseImageUploadReturn => {
  const { caseId, isAdmin, componentName = 'useCaseImageUpload' } = options
  const { t } = useTranslation()
  const setToast = useSetToast()
  const handleError = useErrorHandler(componentName)

  const uploadImage = useCallback(
    async (file: File): Promise<{ imageUrl: string }> => {
      const validation = validateCaseImageFile(file)
      if (!validation.valid) {
        if (validation.error.code === ERROR_CODE.FILE_TOO_LARGE) {
          throw new Error(t('toast.fileTooLarge', validation.error.data))
        }
        throw new Error(t('toast.unsupportedImageFormat'))
      }
      const validFile = validation.sanitizedFile

      setToast({ text: t('toast.processingImageForBestQuality') })

      const { url, s3Key } = await getPresignedUploadUrl(caseId, validFile.type)
      await uploadToPresignedUrl(url, validFile)
      const { jobId } = await processImage(caseId, s3Key, validFile.name)
      const result = await pollForCompletion(jobId)

      if (isAdmin) {
        setToast({ text: t('toast.imageEnhancedAndUploaded') })
      } else {
        setToast({ text: t('toast.imageUploaded') })
      }

      return result
    },
    [caseId, isAdmin, t, setToast],
  )

  const uploadMultipleImages = useCallback(
    async (files: File[]): Promise<string[]> => {
      // Validate all files first
      const validFiles: File[] = []
      for (const file of files) {
        const validation = validateCaseImageFile(file)
        if (!validation.valid) {
          const msg =
            validation.error.code === ERROR_CODE.FILE_TOO_LARGE
              ? t('toast.fileTooLarge', validation.error.data)
              : t('toast.unsupportedImageFormat')
          handleError(msg, new Error(msg))
          continue
        }
        validFiles.push(validation.sanitizedFile)
      }

      if (validFiles.length === 0) return []

      setToast({ text: t('toast.processingImageForBestQuality') })

      // 1. Get all presigned URLs in one call
      const { uploads } = await getPresignedUploadUrls(
        caseId,
        validFiles.map((f) => f.type),
      )

      // 2. Upload all files to S3 in parallel
      await Promise.all(
        validFiles.map((file, i) => uploadToPresignedUrl(uploads[i].url, file)),
      )

      // 3. Submit batch for processing (one call)
      const { jobIds } = await processImageBatch(
        caseId,
        uploads.map((u, i) => ({
          s3Key: u.s3Key,
          originalFileName: validFiles[i].name,
        })),
      )

      // 4. Poll all jobs in one call
      const imageUrls = await pollBatchForCompletion(jobIds)

      const successCount = imageUrls.filter((url) => url !== '').length
      if (successCount > 0) {
        setToast({ text: t('toast.imageUploaded') })
      }

      return imageUrls.filter((url) => url !== '')
    },
    [caseId, t, setToast, handleError],
  )

  return {
    uploadImage,
    uploadMultipleImages,
  }
}
